import { createFileRoute, Link } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight } from "lucide-react";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import { buildBlogHead } from "@/lib/blogMeta";
import { Button } from "@/components/ui/button";
import manuscript from "@/content/pcod-vs-pcos-vs-pmos.md?raw";
import hero from "@/assets/blog/pcos-now-pmos-truwe-hero-1672.webp.asset.json";
import heroSmall from "@/assets/blog/pcos-now-pmos-truwe-hero-960.webp.asset.json";
import wider from "@/assets/blog/pmos-more-than-a-late-period-1672.webp.asset.json";
import widerSmall from "@/assets/blog/pmos-more-than-a-late-period-960.webp.asset.json";
import questions from "@/assets/blog/pmos-better-questions-care-plan-1672.webp.asset.json";
import questionsSmall from "@/assets/blog/pmos-better-questions-care-plan-960.webp.asset.json";

const SLUG = "pcod-vs-pcos-vs-pmos";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "PCOD vs PCOS vs PMOS: The 2026 Name Change | TruWe";
const DESCRIPTION = "PCOS is now PMOS. Understand the 2026 name change, PCOD vs PCOS, and why hormones, metabolic health and emotional wellbeing belong in your care.";
const PUBLISHED = "2026-09-21";
// Preserve the original publication date. Update dateModified only on publication.
const MODIFIED = PUBLISHED;
const FAQS = [
  { q: "Is PMOS the same condition as PCOS?", a: "Yes. PMOS is the new name for PCOS. The change was introduced in May 2026 to reflect the condition's wider hormonal and metabolic features." },
  { q: "What is the full form of PMOS?", a: "PMOS stands for polyendocrine metabolic ovarian syndrome." },
  { q: "Does the name change mean my PCOS diagnosis was wrong?", a: "No. The terminology change alone does not invalidate a previous diagnosis. Ask your clinician how the current terminology applies to your records." },
  { q: "Is PCOD a milder form of PMOS?", a: "International guidance does not establish PCOD as a separate, milder diagnosis. Ask the clinician who used the term which findings support it." },
  { q: "Can a supplement cure PMOS?", a: "There is currently no cure. Treatment and lifestyle changes can help manage symptoms and longer-term health. Supplements should not replace prescribed care." },
];

const sourceUrls = [
  "https://www.monash.edu/medicine/mchri/pcos/guideline",
  "https://www.monash.edu/news/articles/polyendocrine-metabolic-ovarian-syndrome-new-name-to-improve-diagnosis-and-care-of-condition-affecting-170-million-women-worldwide",
  "https://www.asrm.org/practice-guidance/practice-committee-documents/recommendations-from-the-2023-international-evidence-based-guideline-for-the-assessment-and-management-of-polyendocrine-metabolic-ovarian-syndrome-2023/",
  "https://www.mayoclinic.org/diseases-conditions/pcos/symptoms-causes/syc-20353439",
  "https://www.who.int/news-room/fact-sheets/detail/polycystic-ovary-syndrome",
  "https://www.nhs.uk/conditions/polyendocrine-metabolic-ovarian-syndrome-pmos/",
  "https://truwe.in/products/ova-balance",
  "https://www.nccih.nih.gov/health/berberine-and-weight-loss-what-you-need-to-know",
];
function trackArticleCta() {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: "article_product_cta_click", article_slug: SLUG, cta_placement: "inline", product_handle: "ova-balance" });
}

const content = manuscript
  .replace(/^# .+\n/, "")
  .replace(/^By TruWe\s*\n/m, "")
  .replace(/^\*\*Your cycle is only part of the story\. Your care should reflect that\.\*\*\s*\n/m, "")
  .replace(/\[(\d+(?:,\s*\d+)*)\](?!\()/g, (_, citations: string) =>
    citations.split(/,\s*/).map((n) => `[${n}](${sourceUrls[Number(n) - 1]})`).join(", "),
  );

const images: Record<string, { url: string; small: string }> = {
  "assets/pcos-now-pmos-truwe-hero.png": { url: hero.url, small: heroSmall.url },
  "assets/pmos-more-than-a-late-period.png": { url: wider.url, small: widerSmall.url },
  "assets/pmos-better-questions-care-plan.png": { url: questions.url, small: questionsSmall.url },
};

export const Route = createFileRoute("/blog/pcod-vs-pcos-vs-pmos")({
  component: Article,
  head: () => {
    const base = buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: "PCOS is now PMOS and it is more than an ovarian condition",
      ogDescription: "A clearer name for a condition that reaches beyond the ovaries. Understand what changed and what to ask at your next appointment.",
      headline: "PCOS is now PMOS and it is more than an ovarian condition",
      breadcrumbName: "PCOD vs PCOS vs PMOS",
      datePublished: PUBLISHED,
      dateModified: MODIFIED,
      authorName: "TruWe",
      articleType: "BlogPosting",
      faqs: FAQS,
      ogImage: `https://truwe.in${hero.url}`,
    });
    return {
      ...base,
      meta: [...base.meta, { name: "twitter:card", content: "summary_large_image" }, { name: "twitter:image", content: `https://truwe.in${hero.url}` }],
    };
  },
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <p className="text-xs font-semibold uppercase text-brand">Clinical Library · Hormonal Health</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-brand-deep md:text-5xl">
          PCOS is now PMOS and it is more than an ovarian condition
        </h1>
        <p className="mt-4 text-lg font-medium leading-relaxed text-foreground">Your cycle is only part of the story. Your care should reflect that.</p>
        <BlogByline author="TruWe" date={PUBLISHED} readingTime="8 min read" sources="Monash University, international clinical guidance, WHO, NHS, Mayo Clinic and NCCIH; sources linked below." />
        <div className="mt-4"><ReadWithLlm title={TITLE} url={URL} kind="article" /></div>
        <div className="pmos-article mt-8 text-base leading-8 text-foreground/85 md:text-[17px]">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            h2: ({ children }) => <h2 className="mt-12 mb-4 font-display text-2xl font-extrabold leading-tight text-brand-deep md:text-3xl">{children}</h2>,
            h3: ({ children }) => <h3 className="mt-8 mb-3 font-display text-xl font-bold text-brand-deep">{children}</h3>,
            p: ({ children }) => <p className="my-4">{children}</p>,
            ul: ({ children }) => <ul className="my-5 list-disc space-y-2 pl-6 marker:text-brand">{children}</ul>,
            ol: ({ children }) => <ol className="my-5 list-decimal space-y-3 pl-6 marker:text-brand">{children}</ol>,
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-brand-deep">{children}</strong>,
            em: ({ children }) => <em className="text-muted-foreground">{children}</em>,
            a: ({ href, children }) => {
              if (href === "https://truwe.in/products/ova-balance" && String(children).startsWith("Explore")) {
                return <Button asChild className="my-4 h-auto min-h-11 whitespace-normal px-5 py-3 text-left"><Link to="/products/$handle" params={{ handle: "ova-balance" }} onClick={trackArticleCta}>{children}<ArrowRight aria-hidden /></Link></Button>;
              }
              return <a href={href} target={href?.startsWith("https://truwe.in/") ? undefined : "_blank"} rel={href?.startsWith("https://truwe.in/") ? undefined : "noopener noreferrer"} className="text-brand underline underline-offset-2 hover:text-brand-deep">{children}</a>;
            },
            img: ({ src, alt }) => {
              const image = images[src ?? ""];
              if (!image) return null;
              return <img src={image.url} srcSet={`${image.small} 960w, ${image.url} 1672w`} sizes="(max-width: 768px) 100vw, 768px" alt={alt ?? ""} width={1672} height={941} loading={src?.includes("hero") ? "eager" : "lazy"} decoding="async" className="my-8 h-auto w-full rounded-md border border-border" />;
            },
            table: ({ children }) => <div className="my-6 overflow-x-auto border-y border-border"><table className="w-full min-w-[560px] border-collapse text-left text-sm leading-6">{children}</table></div>,
            th: ({ children }) => <th className="border-b border-border bg-muted/30 px-4 py-3 font-semibold text-brand-deep">{children}</th>,
            td: ({ children }) => <td className="border-b border-border px-4 py-3 align-top">{children}</td>,
          }}>{content}</ReactMarkdown>
        </div>
        <RelatedReading articles={relatedArticles(SLUG)} />
      </div>
    </article>
  );
}
